import React, { useMemo, useRef } from 'react';
import { Button as RSButton } from 'reactstrap';
import { IoRefresh } from 'react-icons/io5';
import { CiExport } from 'react-icons/ci';
import { BsFiletypeCsv, BsFiletypePdf } from 'react-icons/bs';
import { Dropdown, filterBarSelectStyles } from '@/components/Dropdown';
import { TimezoneDropdown } from '@/components/TimezoneDropdown';
import { SingleDatePicker } from '@/components/SingleDatePicker';
import { Button, buttonVariants } from '@/components/Button';
import { ActionsMenu } from '@/components/ActionsMenu';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import styles from './ReportFiltersBar.module.scss';

// Config-driven so adding a future export format (e.g. XLSX) is a one-line
// addition here plus a new `exportFormats` entry from the caller, not a new
// button/branch per format.
const EXPORT_FORMAT_CONFIG = {
  csv: { label: 'Export as CSV', icon: <BsFiletypeCsv size={16} /> },
  pdf: { label: 'Export as PDF', icon: <BsFiletypePdf size={16} /> },
};

export const ReportFiltersBarComponent = ({
  membersOptions,
  selectedMember,
  onMemberFilterChange,
  membersLoading,
  membersError,
  membersErrorObj,
  refetchMembers,
  timezoneOptions,
  selectedTimezone,
  onTimezoneChange,
  timezonesLoading,
  date,
  onDateChange,
  onRefresh,
  onExport,
  exportFormats = ['csv'],
  exportLoading,
  exportDisabled,
  showExport = true,
}) => {
  const exportMenuRef = useRef(null);
  const isMultiFormatExport = exportFormats.length > 1;

  const exportMenuItems = useMemo(
    () =>
      exportFormats.map((format) => ({
        label: EXPORT_FORMAT_CONFIG[format]?.label ?? format,
        icon: EXPORT_FORMAT_CONFIG[format]?.icon,
        onClick: () => onExport?.(format),
      })),
    [exportFormats, onExport]
  );

  const handleExportButtonClick = (e) => {
    if (isMultiFormatExport) {
      exportMenuRef.current?.toggle(e);
    } else {
      onExport?.(exportFormats[0]);
    }
  };

  return (
    <div className={styles.filtersContainer}>
      <div className={styles.dropdownsContainer}>
        <div className={styles.dropdown}>
          <Dropdown
            options={membersOptions}
            value={selectedMember}
            placeholder="Select member..."
            onChange={onMemberFilterChange}
            isLoading={membersLoading}
            isDisabled={membersLoading || membersError}
            selectStyles={filterBarSelectStyles}
          />
          {membersError && (
            <div className={styles.memberError}>
              <Text
                variant={textVariantsEnum.Text_sm}
                fontWeight={fontWeightEnum.Medium}
              >
                Failed to load members. {membersErrorObj?.message || ''}
              </Text>
              <RSButton color="link" size="sm" onClick={() => refetchMembers()}>
                Retry
              </RSButton>
            </div>
          )}
        </div>
        <div className={styles.dropdown}>
          <TimezoneDropdown
            placeholder="Select timezone..."
            timezoneOptions={timezoneOptions}
            selectedTimezone={selectedTimezone}
            onChange={onTimezoneChange}
            isLoading={timezonesLoading}
            selectStyles={filterBarSelectStyles}
          />
        </div>
        <SingleDatePicker date={date} onDateChange={onDateChange} compact />
      </div>
      <div className={styles.actionsContainer}>
        <Button
          variant={buttonVariants.outline}
          size="xl"
          text="Refresh"
          icon={<IoRefresh size={16} color="var(--gray-700)" />}
          onClick={onRefresh}
        />
        {showExport && (
          <>
            <Button
              variant={buttonVariants.outline}
              size="xl"
              text="Export"
              icon={<CiExport size={16} color="var(--gray-700)" />}
              onClick={handleExportButtonClick}
              loading={exportLoading}
              disabled={exportDisabled}
            />
            {isMultiFormatExport && (
              <ActionsMenu items={exportMenuItems} ref={exportMenuRef} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

ReportFiltersBarComponent.displayName = 'ReportFiltersBar';
